/**
 * SYSTEM SETTINGS MODULE
 * Per-tenant shipping-carrier config (UPS first). Non-secret settings are read
 * directly from the account-scoped `shipping_carrier_settings` table over RLS;
 * credentials are written ONLY through the `carrier-connect` edge function (the
 * client never inserts the secret, and the secret is never read back). This is
 * the same split as the Google Sheets integration (settings table + edge fn).
 *
 * The pos-fulfillment module consumes this config through the
 * `create-shipping-label` edge function — a documented service boundary, not a
 * cross-module import.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** The carrier settings row (non-secret) for this account, or null. */
export async function getCarrierSettings(carrier = "ups") {
  const account = await acctId();
  const { data, error } = await supabase
    .from("shipping_carrier_settings")
    .select("*")
    .eq("account_id", account)
    .eq("carrier", carrier)
    .maybeSingle();
  if (error) { console.error("[shippingCarrierService.getCarrierSettings]", error); throw new Error("Could not load shipping settings."); }
  return data;
}

// ⚠️ Every carrier-connect call MUST name the workspace it is acting on.
// The function holds the service-role key and bypasses RLS, and it cannot see
// the workspace switcher (a browser-side preference). Without an explicit id it
// falls back to guessing the caller's "primary" workspace — which, for anyone in
// two workspaces, wrote one tenant's UPS credentials into the other. The
// function re-verifies membership of whatever we send, so this is a correctness
// signal, not a trust boundary.

/** Save settings (+ credentials when clientId/clientSecret are present). */
export async function saveCarrier(payload) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("carrier-connect", {
    body: { action: "save", carrier: "ups", ...payload, accountId },
  });
  if (error) throw new Error(error.message || "Could not save shipping settings.");
  if (!data?.ok) throw new Error(data?.error || "Could not save shipping settings.");
  return data; // { ok, verified, has_credentials }
}

/** Verify the UPS credentials (live OAuth token call) without saving. */
export async function testCarrier(payload) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("carrier-connect", {
    body: { action: "test", carrier: "ups", ...payload, accountId },
  });
  if (error) throw new Error(error.message || "Connection test failed.");
  if (!data?.ok) throw new Error(data?.error || "Connection test failed.");
  return data; // { ok, verified }
}

/** Remove stored credentials and disable the carrier. */
export async function disconnectCarrier(carrier = "ups") {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("carrier-connect", {
    body: { action: "disconnect", carrier, accountId },
  });
  if (error) throw new Error(error.message || "Could not disconnect.");
  if (!data?.ok) throw new Error(data?.error || "Could not disconnect.");
  return data;
}
